import { DIAGRAM_ARROW_MAX_POINTS, DIAGRAM_PITCH_HEIGHT, type DiagramArrowKind, type DiagramElement, type DiagramPitch } from '@ukinfo/shared';
import { clamp, toCoordinate, type FieldPoint } from './diagramEdit';

/**
 * Freihand-Pfeile: Wer beim Zeichnen eines Pfeils eine Kurve zieht, bekommt einen weichen Pfeil, dessen Punkte aus dem
 * gezogenen Weg vereinfacht werden (Ramer-Douglas-Peucker). Reine Rechnung ohne React. Pass und Schuss werden nie freihand
 * gezeichnet (`DRAWN_STRAIGHT`): Ziehen ergibt dort immer einen geraden Pfeil, Knicke setzt man nur ausdruecklich.
 *
 * Gerechnet wird in Zeichnungseinheiten (x und y gleich gewichtet, `DIAGRAM_PITCH_HEIGHT`), die Punkte kommen und gehen als
 * Feldkoordinaten (Prozent, Vielfache von 0,5).
 */

type Arrow = Extract<DiagramElement, { type: 'arrow' }>;
type Vertex = [number, number];

/** Pfeilarten, die beim Zeichnen immer gerade bleiben: Ziehen macht keinen Freihand-Pfeil, ein weiterer Tipp keinen Knick (der hohe Ball hat ohnehin keine Knicke). */
export const DRAWN_STRAIGHT: readonly DiagramArrowKind[] = ['PASS', 'SHOT', 'LOFT'];

/** Weicht der gezogene Weg um mehr als so viele Zeichnungseinheiten von der Geraden ab, ist es ein Freihand-Pfeil (sonst ein gerader). */
export const FREEHAND_MIN_DEVIATION = 4;
/** Ein Freihand-Pfeil hat mindestens so viele Zwischenpunkte ... */
export const FREEHAND_MIN_POINTS = 2;
/** ... und hoechstens so viele (mehr nimmt der Pfeil nicht auf). */
export const FREEHAND_MAX_POINTS = DIAGRAM_ARROW_MAX_POINTS;

type Flat = { x: number; y: number };

function toUnits(path: readonly FieldPoint[], pitch: DiagramPitch): Flat[] {
  const scale = DIAGRAM_PITCH_HEIGHT[pitch] / 100;
  return path.map((point) => ({ x: point.x, y: point.y * scale }));
}

/** Abstand eines Punktes von der Strecke a-b. */
function distanceToSegment(point: Flat, a: Flat, b: Flat): number {
  const lengthSquared = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
  const t = lengthSquared === 0 ? 0 : clamp(((point.x - a.x) * (b.x - a.x) + (point.y - a.y) * (b.y - a.y)) / lengthSquared, 0, 1);
  return Math.hypot(point.x - (a.x + (b.x - a.x) * t), point.y - (a.y + (b.y - a.y) * t));
}

/** Groesster Abstand der Punkte des Weges von der Geraden (Strecke) zwischen seinem ersten und letzten Punkt, in Zeichnungseinheiten. */
export function pathDeviation(path: readonly FieldPoint[], pitch: DiagramPitch): number {
  if (path.length < 3) return 0;
  const flat = toUnits(path, pitch);
  const first = flat[0]!;
  const last = flat[flat.length - 1]!;
  let worst = 0;
  for (const point of flat) worst = Math.max(worst, distanceToSegment(point, first, last));
  return worst;
}

/** Ramer-Douglas-Peucker: die Indizes der behaltenen Punkte (immer erster und letzter), aufsteigend. */
function rdp(points: readonly Flat[], epsilon: number): number[] {
  const keep = new Set<number>([0, points.length - 1]);
  const stack: Array<[number, number]> = [[0, points.length - 1]];
  while (stack.length > 0) {
    const [from, to] = stack.pop()!;
    let worst = -1;
    let at = -1;
    for (let index = from + 1; index < to; index += 1) {
      const gap = distanceToSegment(points[index]!, points[from]!, points[to]!);
      if (gap > worst) {
        worst = gap;
        at = index;
      }
    }
    if (at >= 0 && worst > epsilon) {
      keep.add(at);
      stack.push([from, at], [at, to]);
    }
  }
  return [...keep].sort((a, b) => a - b);
}

/** Der Punkt des Weges nach dem Anteil `fraction` (0 bis 1) seiner Laenge, in Feldkoordinaten. */
function atFraction(path: readonly FieldPoint[], flat: readonly Flat[], fraction: number): FieldPoint {
  const offsets: number[] = [0];
  for (let index = 1; index < flat.length; index += 1) offsets.push(offsets[index - 1]! + Math.hypot(flat[index]!.x - flat[index - 1]!.x, flat[index]!.y - flat[index - 1]!.y));
  const goal = offsets[offsets.length - 1]! * fraction;
  let index = 1;
  while (index < offsets.length - 1 && offsets[index]! < goal) index += 1;
  return path[index] ?? path[path.length - 1]!;
}

/**
 * Die Zwischenpunkte (2 bis 8) eines gezogenen Weges: Ramer-Douglas-Peucker mit einer Toleranz, die so lange waechst,
 * bis hoechstens 8 Punkte bleiben, und so lange schrumpft, bis es mindestens 2 sind. Gibt es auch dann weniger (ein einfacher
 * Bogen), kommen Punkte bei einem Drittel und zwei Dritteln der Weglaenge dazu. Die Punkte liegen auf dem 0,5er-Raster, ohne
 * Doppelte und ohne Anfang und Ende.
 */
export function simplifyPath(path: readonly FieldPoint[], pitch: DiagramPitch): FieldPoint[] {
  if (path.length < 3) return [];
  const flat = toUnits(path, pitch);
  const first = path[0]!;
  const last = path[path.length - 1]!;
  const inner = (epsilon: number): FieldPoint[] => rdp(flat, epsilon).slice(1, -1).map((index) => path[index]!);
  let epsilon = Math.max(pathDeviation(path, pitch) / 2, 0.6);
  let kept = inner(epsilon);
  // Zu wenige: feiner rechnen (bis die Toleranz unter einem halben Raster liegt).
  while (kept.length < FREEHAND_MIN_POINTS && epsilon > 0.5) {
    epsilon /= 1.6;
    kept = inner(epsilon);
  }
  // Zu viele: groeber rechnen.
  while (kept.length > FREEHAND_MAX_POINTS) {
    epsilon *= 1.25;
    kept = inner(epsilon);
  }
  if (kept.length < FREEHAND_MIN_POINTS) kept = [atFraction(path, flat, 1 / 3), atFraction(path, flat, 2 / 3)];
  const points: FieldPoint[] = [];
  for (const point of kept) {
    const rounded = { x: toCoordinate(point.x), y: toCoordinate(point.y) };
    const before = points[points.length - 1] ?? first;
    if (rounded.x === before.x && rounded.y === before.y) continue;
    if (rounded.x === last.x && rounded.y === last.y) continue;
    points.push(rounded);
  }
  return points;
}

/**
 * Der Freihand-Pfeil zu einem gezogenen Weg: ein weicher Pfeil mit den vereinfachten Punkten als Knickpunkten. `null`, wenn der Weg
 * kaum von der Geraden abweicht (dann bleibt es ein gerader Pfeil), bei Pass und Schuss (sie bleiben immer gerade) und beim hohen Ball
 * (er hat keine Knickpunkte) oder wenn nach dem Runden weniger als ein Zwischenpunkt bleibt.
 */
export function freehandArrow(kind: DiagramArrowKind, path: readonly FieldPoint[], pitch: DiagramPitch): Arrow | null {
  if (DRAWN_STRAIGHT.includes(kind) || path.length < 3) return null;
  if (pathDeviation(path, pitch) <= FREEHAND_MIN_DEVIATION) return null;
  const from = path[0]!;
  const to = path[path.length - 1]!;
  const points = simplifyPath(path, pitch);
  if (points.length === 0) return null;
  return { type: 'arrow', kind, from: [from.x, from.y], to: [to.x, to.y], points: points.map((point): Vertex => [point.x, point.y]), smooth: true };
}
